import React, { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react';

interface LoadingContextType {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  showLoading: () => void;
  hideLoading: () => void;
  shouldShowLoading: (route: string) => boolean;
  markRouteAsVisited: (route: string) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const visitedRoutes = useRef<Set<string>>(new Set());

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('visitedRoutes');
      if (stored) {
        const routes = JSON.parse(stored);
        visitedRoutes.current = new Set(routes);
      }
    } catch (error) {
      console.warn('Failed to load visited routes from sessionStorage:', error);
    }
  }, []);

  const showLoading = () => setIsLoading(true);
  const hideLoading = () => setIsLoading(false);

  const shouldShowLoading = (route: string): boolean => {
    if (visitedRoutes.current.size === 0) {
      return true;
    }
    return !visitedRoutes.current.has(route);
  };

  const markRouteAsVisited = (route: string) => {
    visitedRoutes.current.add(route);
        try {
      sessionStorage.setItem('visitedRoutes', JSON.stringify(Array.from(visitedRoutes.current)));
    } catch (error) {
      console.warn('Failed to save visited routes to sessionStorage:', error);
    }
  };

  return (
    <LoadingContext.Provider value={{ 
      isLoading, 
      setIsLoading, 
      showLoading, 
      hideLoading, 
      shouldShowLoading, 
      markRouteAsVisited 
    }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
}; 